<template>
  <div>
    <!-- La piantina resta sempre disegnata (sotto): questi alert sono solo un
         cappello informativo, non condizionano più il rendering del canvas. -->
    <template v-if="filtri.id_fitofarmaco">
      <v-progress-linear v-if="loading" indeterminate class="mb-2" />
      <template v-else>
        <v-alert
          v-if="eventi_prodotto.length"
          type="success"
          outlined
          dense
          class="mb-2"
        >
          {{ eventi_prodotto.length }}
          {{ eventi_prodotto.length === 1 ? "intervento trovato" : "interventi trovati" }}
          su {{ totale_serre }}
          {{ totale_serre === 1 ? "serra" : "serre" }}.
        </v-alert>
        <v-alert v-else type="info" outlined dense class="mb-2">
          Nessun trattamento trovato per questo prodotto nel periodo selezionato.
        </v-alert>
        <v-alert v-if="eventi_non_mappabili.length" type="warning" outlined dense class="mb-2">
          {{ eventi_non_mappabili.length }}
          {{ eventi_non_mappabili.length === 1 ? "intervento" : "interventi" }}
          registrati solo sulla vecchia Zona (non mostrati sulla mappa):
          <v-chip
            v-for="id_zona in zone_non_mappabili"
            :key="id_zona"
            x-small
            outlined
            color="orange darken-2"
            class="ml-1"
            >{{ nomeZona(id_zona) }}</v-chip
          >
        </v-alert>
      </template>
    </template>

    <AppezzamentoCanvas
      v-if="appezzamento"
      :appezzamento="appezzamento"
      :posizioni="posizioni"
      :serre="serre"
      :readonly="true"
      :hide_toolbar="true"
      :flat_mode="true"
      :evidenziate="serre_evidenziate"
      :badge_per_serra="badge_map"
      :hover_extra_per_serra="hover_map"
    />
  </div>
</template>

<script>
import moment from "moment";
import AppezzamentoCanvas from "@/components/PianoColtivazione/Desktop/AppezzamentoCanvas.vue";

// Layer "Trattamenti": riceve i parametri già risolti da TrattamentiFiltri
// (v-model condiviso col genitore MappAziendaleDesktop) e si occupa solo di
// incrociare i dati QDC e renderizzare mappa/badge/tooltip. Nessuna UI di
// filtro qui: la maschera e i suoi parametri vivono nel genitore, così le
// prossime "maschere" seguono lo stesso schema (filtri / layer separati).
export default {
  name: "MappaTrattamenti",
  components: { AppezzamentoCanvas },
  props: {
    appezzamento: { type: Object, default: null },
    posizioni: { type: Array, default: () => [] },
    serre: { type: Array, default: () => [] },
    // { id_fitofarmaco, ts_inizio, ts_fine } — risolto da TrattamentiFiltri.
    filtri: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    periodo_key() {
      return this.filtri.ts_inizio && this.filtri.ts_fine
        ? `${this.filtri.ts_inizio}_${this.filtri.ts_fine}`
        : null;
    },
    eventi_periodo() {
      return this.$store.getters.get_eventi_qdc;
    },
    eventi_prodotto() {
      if (!this.filtri.id_fitofarmaco) return [];
      return this.eventi_periodo.filter((evento) =>
        (evento.fitofarmaci || []).some((f) => f.id_fitofarmaco == this.filtri.id_fitofarmaco)
      );
    },
    // { [id_serra]: { count, date: [ts,...] } }
    trattamenti_per_serra() {
      const map = {};
      this.eventi_prodotto.forEach((evento) => {
        (evento.id_serre || []).forEach((id_serra) => {
          if (!map[id_serra]) map[id_serra] = { count: 0, date: [] };
          map[id_serra].count += 1;
          map[id_serra].date.push(evento.ts);
        });
      });
      Object.values(map).forEach((v) => v.date.sort((a, b) => a - b));
      return map;
    },
    serre_evidenziate() {
      return Object.keys(this.trattamenti_per_serra);
    },
    totale_serre() {
      return this.serre_evidenziate.length;
    },
    badge_map() {
      const out = {};
      Object.entries(this.trattamenti_per_serra).forEach(([id, v]) => {
        out[id] = v.count;
      });
      return out;
    },
    hover_map() {
      const out = {};
      Object.entries(this.trattamenti_per_serra).forEach(([id, v]) => {
        out[id] = {
          titolo: v.count === 1 ? "1 intervento" : `${v.count} interventi`,
          righe: v.date.map((ts) => moment.unix(ts).format("DD/MM/YYYY")),
        };
      });
      return out;
    },
    // Eventi che matchano il prodotto ma non referenziano nessuna serra (solo
    // la vecchia Zona di coltivazione): non mappabili, ma vanno comunque
    // segnalati invece di sparire silenziosamente dal conteggio.
    eventi_non_mappabili() {
      return this.eventi_prodotto.filter(
        (e) => (!e.id_serre || !e.id_serre.length) && e.id_zone && e.id_zone.length
      );
    },
    zone_non_mappabili() {
      return [...new Set(this.eventi_non_mappabili.flatMap((e) => e.id_zone))];
    },
  },
  watch: {
    periodo_key: {
      immediate: true,
      handler(val) {
        if (val) this.fetchPeriodo();
      },
    },
  },
  methods: {
    fetchPeriodo() {
      this.loading = true;
      this.$store
        .dispatch("fetch_eventi_qdc", { ts_inizio: this.filtri.ts_inizio, ts_fine: this.filtri.ts_fine })
        .finally(() => {
          this.loading = false;
        });
    },
    nomeZona(id) {
      const zona = this.$store.getters.get_zona_by_id(id);
      return zona ? zona.nome : id;
    },
  },
};
</script>
